import knowledgeBase from "../data/supportKnowledgeBase";

/**
 * English stop words to ignore during matching
 */
const STOP_WORDS = new Set([
  "i", "me", "my", "do", "does", "did", "is", "are", "was", "were",
  "be", "been", "being", "have", "has", "had", "a", "an", "the",
  "and", "or", "but", "if", "in", "on", "at", "to", "for", "of",
  "with", "by", "from", "as", "into", "about", "can", "will",
  "would", "should", "could", "what", "how", "where", "when",
  "which", "who", "why", "this", "that", "it", "its", "not",
  "no", "so", "up", "out", "just", "also", "than", "very",
  "want", "need", "please", "tell", "show", "help", "know",
]);

/**
 * Dutch stop words to ignore during matching
 */
const STOP_WORDS_NL = new Set([
  "ik", "je", "jij", "mijn", "een", "het", "de", "en", "of", "maar",
  "als", "in", "op", "aan", "voor", "van", "met", "door", "uit", "over",
  "kan", "wil", "zou", "moet", "wat", "hoe", "waar", "wanneer", "welke",
  "wie", "waarom", "dit", "dat", "er", "niet", "geen", "zo", "ook",
  "dan", "zeer", "graag", "alsjeblieft", "vertellen", "laten", "zien",
  "helpen", "weten",
]);

/**
 * Detect whether the query is Dutch or English.
 * Simple heuristic: count how many tokens match each stop word set,
 * and also check for common Dutch question starters.
 */
function detectLanguage(text) {
  const words = text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 0);

  if (words.length === 0) return "en";

  // Check for common Dutch question starters
  const dutchStarters = ["hoe", "wat", "waar", "wanneer", "welke"];
  if (dutchStarters.includes(words[0])) {
    // If the first word is a Dutch question starter, lean Dutch
    // but still count to be sure
    let nlCount = 1;
    let enCount = 0;
    for (const word of words.slice(1)) {
      if (STOP_WORDS_NL.has(word)) nlCount++;
      if (STOP_WORDS.has(word)) enCount++;
    }
    // Dutch starters like "hoe" and "wat" also appear in English stop words,
    // so give Dutch a boost when the first word is a Dutch starter
    return nlCount >= enCount ? "nl" : "en";
  }

  let nlCount = 0;
  let enCount = 0;
  for (const word of words) {
    if (STOP_WORDS_NL.has(word)) nlCount++;
    if (STOP_WORDS.has(word)) enCount++;
  }

  // Words that appear in both sets shouldn't tip the balance,
  // so we check for uniquely Dutch words too
  const uniquelyDutch = ["ik", "je", "jij", "mijn", "een", "het", "de", "en",
    "maar", "voor", "van", "met", "door", "uit", "over", "kan", "wil", "zou",
    "moet", "wanneer", "welke", "wie", "waarom", "dit", "dat", "er", "niet",
    "geen", "ook", "dan", "zeer", "graag", "alsjeblieft", "vertellen", "laten",
    "zien", "helpen", "weten"];
  const hasUniquelyDutch = words.some((w) => uniquelyDutch.includes(w));

  if (hasUniquelyDutch && nlCount > enCount) return "nl";
  if (nlCount > enCount) return "nl";

  return "en";
}

/**
 * Tokenize and normalize a string, filtering out stop words for the given language
 */
function tokenize(text, language = "en") {
  const stopWords = language === "nl" ? STOP_WORDS_NL : STOP_WORDS;
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((word) => word.length > 1 && !stopWords.has(word));
}

/**
 * Calculate a match score between user query tokens and a KB entry
 */
function scoreEntry(queryTokens, entry, language = "en") {
  let score = 0;
  const keywordsField = language === "nl" && entry.keywords_nl ? entry.keywords_nl : entry.keywords;
  const entryKeywords = keywordsField.map((k) => k.toLowerCase());
  const questionTokens = tokenize(entry.question, "en"); // question is always English

  for (const token of queryTokens) {
    // Exact keyword match (highest weight)
    for (const keyword of entryKeywords) {
      if (keyword === token) {
        score += 3;
      } else if (keyword.includes(token) || token.includes(keyword)) {
        score += 1.5;
      }
    }

    // Question text match
    for (const qToken of questionTokens) {
      if (qToken === token) {
        score += 1;
      } else if (qToken.includes(token) || token.includes(qToken)) {
        score += 0.5;
      }
    }
  }

  // Also match against English keywords when language is Dutch
  // (users may mix Dutch and English terms like "ticket", "VIP", "stripe")
  if (language === "nl" && entry.keywords_nl) {
    const englishKeywords = entry.keywords.map((k) => k.toLowerCase());
    for (const token of queryTokens) {
      for (const keyword of englishKeywords) {
        if (keyword === token) {
          score += 2; // slightly lower weight than primary language match
        } else if (keyword.includes(token) || token.includes(keyword)) {
          score += 1;
        }
      }
    }
  }

  // Bonus for matching multiple unique keywords (covers the topic well)
  const matchedKeywords = entryKeywords.filter((kw) =>
    queryTokens.some((t) => kw === t || kw.includes(t) || t.includes(kw))
  );
  if (matchedKeywords.length >= 2) {
    score += matchedKeywords.length * 0.5;
  }

  return score;
}

/**
 * Search the knowledge base for the best matching answer(s).
 * Returns the top match if score is above threshold, or a fallback message.
 */
export function searchKnowledgeBase(query) {
  const language = detectLanguage(query);
  const queryTokens = tokenize(query, language);

  if (queryTokens.length === 0) {
    return {
      found: false,
      answer: language === "nl"
        ? "Kun je je vraag anders formuleren? Ik heb het niet helemaal begrepen."
        : "Could you please rephrase your question? I didn't quite understand that.",
      suggestions: [],
    };
  }

  // Score all entries
  const scored = knowledgeBase
    .map((entry) => ({
      ...entry,
      score: scoreEntry(queryTokens, entry, language),
    }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score);

  if (scored.length === 0 || scored[0].score < 2) {
    // No good match — suggest related topics
    const randomSuggestions = knowledgeBase
      .sort(() => Math.random() - 0.5)
      .slice(0, 3)
      .map((e) => e.question);

    return {
      found: false,
      answer: language === "nl"
        ? "Daar ben ik niet zeker van. Probeer te vragen over het aanmaken van evenementen, tickettypes, prijzen, betalingen of gastenbeheer.\n\nJe kunt ook ons ondersteuningsteam bereiken via **support@tikitly.com** voor specifieke hulp."
        : "I'm not sure about that. Try asking about creating events, ticket types, pricing, payments, or guest management.\n\nYou could also contact our support team at **support@tikitly.com** for specific help.",
      suggestions: randomSuggestions,
    };
  }

  const bestMatch = scored[0];

  // If there are close secondary matches, suggest them
  const relatedQuestions = scored
    .slice(1, 4)
    .filter((e) => e.score >= 2)
    .map((e) => e.question);

  // If the entry has a follow-up question, return that instead of the direct answer
  if (bestMatch.followUp) {
    const followUp = language === "nl" && bestMatch.followUp_nl
      ? bestMatch.followUp_nl
      : bestMatch.followUp;
    const fallbackAnswer = language === "nl" && bestMatch.answer_nl
      ? bestMatch.answer_nl
      : bestMatch.answer;

    return {
      found: true,
      hasFollowUp: true,
      followUp: followUp,
      fallbackAnswer: fallbackAnswer,
      matchedQuestion: bestMatch.question,
      suggestions: relatedQuestions,
    };
  }

  const answer = language === "nl" && bestMatch.answer_nl
    ? bestMatch.answer_nl
    : bestMatch.answer;

  return {
    found: true,
    answer: answer,
    matchedQuestion: bestMatch.question,
    suggestions: relatedQuestions,
  };
}
